import { Component, Fragment } from 'react'
import { Box, BoxProps, styled } from '@mui/material'
import { DataGrid, DataGridProps, GridColDef, GridFeatureMode, GridFilterOperator } from '@mui/x-data-grid'
import { GridSortModel, GridValidRowModel, GridRowIdGetter, GridRowSelectionModel } from '@mui/x-data-grid'
import { GridCallbackDetails, GridColumnVisibilityModel, GridDensity, GridFilterModel, GridPaginationModel } from '@mui/x-data-grid'
import type { ComponentType, ReactNode } from 'react'
import { mergeObjects } from '../utils'
import { TableBaseContext } from './context'
import { IActionRowProps } from './create.action-row'
import { customFilterOperators } from './custom.filter-operators'
import { GlobalModal, mapGlobalModalContext } from '../api-context'
import { ICustomGridColDef, ICustomGridFilterModel, ICustomGridSortModel, ITableData, ITableQueryParams } from './types'
import TableHelper from './helpers'
import ToolbarPannel, { IToolbarPannelProps } from './toolbar-pannel'
import { dinoTableClasses, TableStyled } from './styled'

export interface ITableSlots<T> {
  maxSelection?: number
  density?: GridDensity
  toolbar?: ComponentType<IToolbarPannelProps>
  actionRow?: ComponentType<IActionRowProps<T>>
  toolbarProps?: IToolbarPannelProps
  dataGridProps?: DataGridProps
  wrapProps?: BoxProps
}

export type ITableParamsSlots<T> = Pick<ITableSlots<T>, 'maxSelection' | 'toolbarProps' | 'dataGridProps' | 'wrapProps'>

export interface ITableProps<T> {
  data?: ITableData<T>
  query?: ITableQueryParams<T>
  loading?: boolean
  onChange?: (query: ITableQueryParams<T>) => void
  onRowSelectionChange?: DataGridProps['onRowSelectionModelChange']
  slots?: ITableSlots<T>
}

export interface ITableParams<T extends GridValidRowModel> extends ITableParamsSlots<T> {
  featureMode?: GridFeatureMode
  getRowId: GridRowIdGetter<T>
  columns: ICustomGridColDef<T>
  filterOperators?: (config: GridColDef) => GridFilterOperator[]
  columnVisibilityModel?: Partial<Record<keyof T, boolean>>
  actionRow?: Omit<GridColDef, 'field'>
  disableActionRow?: boolean
  disableGlobalModalProvider?: boolean
}

export function CreateTable<T extends GridValidRowModel>(params: ITableParams<T>): ComponentType<ITableProps<T>> {
  const generateColumns = (extendColDefs?: GridColDef[]) => {
    const columns = Object.keys(params.columns).map<GridColDef>((key) => {
      const obj = { field: key, ...params.columns[key] }
      obj.filterOperators = params.filterOperators ? params.filterOperators(obj) : customFilterOperators(obj)
      return obj
    })

    if (!!extendColDefs && extendColDefs.length > 0) columns.push(...extendColDefs)
    return columns
  }

  class Table extends Component<ITableProps<T>> {
    private tableQueryParams: ITableQueryParams<T>
    // private tableQueryParamsCache: ITableQueryParams<T> = {}
    private defaultTableQueryParams: ITableQueryParams<T>
    private columns: GridColDef<T>[]
    private rowSelecteds: GridRowSelectionModel
    private changeTimeout?: ReturnType<typeof setTimeout>
    constructor(props: ITableProps<T>) {
      super(props)
      this.columns = this.initialColumns()
      this.tableQueryParams = { ...props.query }
      this.defaultTableQueryParams = this.tableQueryParams
      this.rowSelecteds = []
    }

    get mergeConfig() {
      const toolbarPannelProps = mergeObjects({}, params?.toolbarProps, this.props.slots?.toolbarProps)
      return {
        toolbar: this.props.slots?.toolbar ?? (() => <ToolbarPannel {...toolbarPannelProps} />),
        ActionRow: this.props.slots?.actionRow,
        disableActionRow: params.disableActionRow,
        columnVisibilityModel: params.columnVisibilityModel,
        maxSelcion: this.props.slots?.maxSelection ?? params.maxSelection,
        dataGridProps: mergeObjects({}, params.dataGridProps, this.props.slots?.dataGridProps),
        wrapProps: mergeObjects({}, params.wrapProps, this.props.slots?.wrapProps),
        loading: (this.props.loading !== undefined ? this.props.loading : this.tableQueryParams?.loading) ?? false
      }
    }

    setTableQueryParams = (value: Partial<ITableQueryParams<T>>) => {
      this.tableQueryParams = mergeObjects({}, this.tableQueryParams, value)
    }

    componentWillUnmount() {
      if (this.changeTimeout) {
        clearTimeout(this.changeTimeout)
      }
    }

    shouldComponentUpdate(nextProps: Readonly<ITableProps<T>>): boolean {
      if (params.featureMode === 'server') {
        // Check if query changed
        if (!TableHelper.equalTableQueryParams(nextProps.query, this.props.query)) {
          this.setTableQueryParams(nextProps.query ?? {})
          return true
        }
        // Check if data or loading changed
        if (JSON.stringify(nextProps.data) !== JSON.stringify(this.props.data) || nextProps.loading !== this.props.loading) {
          return true
        }
        return false
      } else {
        return true
      }
    }

    render() {
      const rootClasses = [dinoTableClasses.root, this.mergeConfig.wrapProps?.className]
      return this.renderWrapContext(
        <TableStyled {...this.mergeConfig.wrapProps} className={rootClasses.filter((x) => !!x).join(' ')}>
          <DataGrid loading={this.props.loading} {...this.getDataGridProps()} />
        </TableStyled>
      )
    }

    renderWrapContext = (children: ReactNode) => {
      const WrapComponent = params.disableGlobalModalProvider ? Fragment : GlobalModal
      return (
        <WrapComponent>
          {mapGlobalModalContext((context) => (
            <TableBaseContext.Provider value={{ showModal: context.show, closeModal: context.close }}>{children}</TableBaseContext.Provider>
          ))}
        </WrapComponent>
      )
    }

    getDataGridProps = (): DataGridProps<T> => {
      const initialState = TableHelper.mapInitialState(this.props.query, {
        columns: { columnVisibilityModel: params.columnVisibilityModel as GridColumnVisibilityModel }
      })
      const obj: DataGridProps<T> = {
        getRowId: params.getRowId,
        initialState,
        columns: this.columns,
        rows: this.props.data?.items ?? [],
        checkboxSelection: true,
        pagination: true,
        density: this.props.slots?.density ?? 'standard',
        filterDebounceMs: 800,
        sx: { border: 0 },
        slots: { toolbar: this.mergeConfig.toolbar },
        rowSelectionModel: this.rowSelecteds,
        onRowSelectionModelChange: this.handleRowSelectionChange
      }
      if (params.featureMode === 'server') {
        const objServer: Partial<DataGridProps<T>> = {
          filterMode: 'server',
          sortingMode: 'server',
          paginationMode: 'server',
          onPaginationModelChange: this.onPaginationModelChange,
          onFilterModelChange: this.onFilterModelChange,
          onSortModelChange: this.onSortModelChange,
          rowCount: this.props.data?.rowTotal ?? 0,
          paginationModel: this.tableQueryParams.pagination,
          filterModel: this.tableQueryParams.filter as GridFilterModel,
          sortModel: TableHelper.mapSortModel(this.tableQueryParams.sort),
          loading: this.mergeConfig.loading
        }
        Object.assign(obj, objServer)
      }
      return mergeObjects(obj, this.mergeConfig.dataGridProps)
    }

    initialColumns = (): GridColDef<T>[] => {
      const { ActionRow, disableActionRow } = this.mergeConfig
      const list: GridColDef<T>[] = []
      if (!!ActionRow && disableActionRow !== true) {
        list.push({
          field: 'Actions',
          minWidth: 100,
          headerAlign: 'right',
          align: 'right',
          filterable: false,
          sortable: false,
          disableExport: true,
          renderCell: (value) => <ActionRow value={value.row} />,
          ...params.actionRow
        })
      }
      return generateColumns(list)
    }

    onPaginationModelChange = (model: GridPaginationModel, details: GridCallbackDetails<'pagination'>) => {
      if (this.mergeConfig.loading) return
      this.setTableQueryParams({ pagination: model, detail: 'pagination' })
      this.handleChange()
    }

    onFilterModelChange = (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
      if (this.mergeConfig.loading) return
      const { pagination } = this.tableQueryParams
      this.setTableQueryParams({
        filter: model as ICustomGridFilterModel<T>,
        pagination: { page: 0, pageSize: pagination?.pageSize ?? 25 },
        detail: TableHelper.detectSearchType(model)
      })
      this.handleChange()
    }

    onSortModelChange = (model: GridSortModel, details: GridCallbackDetails<any>) => {
      if (this.mergeConfig.loading) return
      let sortValue = model as ICustomGridSortModel<T>
      const { sort } = this.tableQueryParams
      if (!!sort?.length && !sortValue.length) {
        if (sort?.[0]?.field === this.defaultTableQueryParams.sort?.[0]?.field) {
          sortValue = [{ field: sort[0]?.field, sort: sort[0]?.sort === 'desc' ? 'asc' : 'desc' }]
        } else {
          sortValue = this.defaultTableQueryParams.sort ?? []
        }
      }
      this.setTableQueryParams({ sort: sortValue, detail: 'sort' })
      this.handleChange()
    }

    handleChange = () => {
      if (this.changeTimeout) clearTimeout(this.changeTimeout)
      this.changeTimeout = setTimeout(() => {
        this.props.onChange && this.props.onChange(this.tableQueryParams)
      }, 300)
    }

    handleRowSelectionChange = (value: GridRowSelectionModel, details: GridCallbackDetails) => {
      if (this.mergeConfig.maxSelcion && value.length > this.mergeConfig.maxSelcion) {
        this.rowSelecteds = value.slice(0, this.mergeConfig.maxSelcion)
      } else {
        this.rowSelecteds = value
      }
      this.props.onRowSelectionChange && this.props.onRowSelectionChange(this.rowSelecteds, details)
      this.forceUpdate()
    }
  }

  return Table
}

export default CreateTable
